<template>
    <div>
        <b-modal
            visible
            :id="modal_id"
            size="xl"
            @cancel="cancel"
            @close="cancel"
            no-close-on-esc
            no-close-on-backdrop
            @shown="focusSearch"
        >
            <template #modal-title>
                Edit Printable - ID: {{selectedPrintableModel.id}} Title: {{selectedPrintableModel.title}}
            </template>

            <div v-if="alternate_printables_loading" class="d-flex justify-content-center">
                <b-spinner label="Loading..."></b-spinner>
            </div>
            <div v-else>
                <b-container class="bv-example-row">
                    <b-row class="justify-content-md-center">
                        <b-col col lg="2" class="d-flex justify-content-end align-items-center p-0">
                            <div>Languages</div>
                            <b-dropdown v-if="can_update_printable" variant="light" no-caret right toggle-class="p-0" class="mx-2">
                                <template v-slot:button-content>
                                    <font-awesome-icon  :icon="['fad', 'plus-square']" size="lg" class="add-language text-success" />
                                </template>
                                <b-dropdown-item
                                    v-if="can_link_printable"
                                    @click="addAlternatePrintable"
                                >
                                    Link to Existing Printable
                                </b-dropdown-item>
                                <b-dropdown-item
                                    v-if="can_link_printable && can_create_printable"
                                    @click="createAlternatePrintable"
                                >
                                    Create New Language Printable
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-col>
                        <b-col cols="auto" class="d-flex justify-content-center align-items-center p-0">
                            <div v-for="(printable, index) in static_printables_array" :key="`alternate-printable-${index}`" style="font-size:2rem" class="language-flag cursor-pointer mx-2 position-relative" @click="selectPrintable(index)">
                                <flag :iso="getFlagIso(printable.language)" />
                                <div
                                    v-if="index === selected_printable_index"
                                    :style="{ borderBottom: '3px solid black', position: 'absolute', bottom: 0, left: 0, right: 0 }"
                                ></div>
                            </div>
                        </b-col>
                        <b-col col lg="2" class="d-flex justify-content-start align-items-center p-0">
                            <b-btn v-if="can_unlink_printable && static_printables_array.length > 1" variant="primary" size="sm" @click="unlinkFromAlternatePrintable">
                                Unlink Language
                            </b-btn>
                        </b-col>
                        <b-btn
                            v-if="selectedPrintableModel.cdn_url"
                            :variant="copy_to_clipboard_btn_variant"
                            :class="{'active': copying_to_clipboard}"
                            content="Copy URL to Clipboard"
                            v-tippy="{placement: 'top'}"
                            @click="copyToClipboard(selectedPrintableModel)"
                            :disabled="copying_to_clipboard"
                            class="my-2 position-absolute copy-btn"
                            size="sm"
                        >
                            <span v-if="copying_to_clipboard">
                                <font-awesome-icon :icon="['fas', 'check']" size="lg" fixed-width />
                            </span>
                            <span v-else>
                                <font-awesome-icon :icon="['fad', 'clipboard']" size="lg" fixed-width />
                            </span>
                        </b-btn>
                    </b-row>
                </b-container>

                <div v-if="!has_english_alt && selectedPrintableModel.type == 'video'"  class="alert alert-danger my-2" role="alert">
                    All {{ languageLabel }} language videos should have an alternate English version. This media is {{ languageLabel }} and lacks an English version.
                </div>
            </div>

            <b-container class="bv-example-row">
                <b-row>
                    <b-col>
                        <b-form-group
                            v-if="can_update_printable"
                            label="Language"
                            label-for="language"
                        >
                            <multiselect
                                v-model="selectedPrintableModel.language"
                                :options="languageOptions"
                                :searchable="false"
                            >
                                <template #option="{ option }">
                                    <span class="d-flex align-items-center">
                                        <div class="mr-1">
                                            <flag v-if="getFlagIso(option)" :iso="getFlagIso(option)" />
                                            <div v-else-if="!option || option == ''">
                                                N/A
                                            </div>
                                        </div>
                                        <div class="text-uppercase">{{ option }}</div>
                                    </span>
                                </template>
                                <template #singleLabel="{ option }">
                                    <span class="d-flex align-items-center">
                                        <div class="mr-1">
                                            <flag v-if="getFlagIso(option)" :iso="getFlagIso(option)" />
                                            <div v-else-if="!option || option == ''">
                                                N/A
                                            </div>
                                        </div>
                                        <div class="text-uppercase">{{ option }}</div>
                                    </span>
                                </template>
                            </multiselect>
                            <b-form-invalid-feedback :state="!selectedPrintableModel.language ? false : null">
                                language is required
                            </b-form-invalid-feedback>
                            <b-form-invalid-feedback :state="languageCheck(selectedPrintableModel.language)">
                                language cannot be changed to a language that is already linked to this printable
                            </b-form-invalid-feedback>
                        </b-form-group>
                        <div class="mb-3 w-100" v-if="can_update_printable">
                            New Printable
                            <b-alert v-if="existing_file_name" show variant="warning">
                                Updates take time to reach the CDN. Check changes to files here:
                                <a :href="s3FileUrl" target="_blank">
                                    AWS Link
                                    <font-awesome-icon
                                        :icon="['fad', 'external-link']"
                                        class="text-warning"
                                        fixed-width
                                    />
                                </a>
                            </b-alert>
                            <b-form-file
                                v-model="submit_data.file"
                                placeholder="Choose a file or drop it here..."
                                drop-placeholder="Drop file here..."
                            ></b-form-file>
                        </div>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <b-form-group
                            label="Title"
                            label-for="title"
                        >
                            <b-form-input
                                type="text"
                                v-model="selectedPrintableModel.title"
                                :disabled="!can_update_printable"
                            />
                            <b-form-invalid-feedback :state="!selectedPrintableModel.title ? false : null">
                                Title is required
                            </b-form-invalid-feedback>
                        </b-form-group>
                        <b-form-group
                        >
                            <label>
                                Merlin ID(s) <sup><font-awesome-icon :icon="['fad', 'info-circle']" content="For multiple Merlin ids, use comma separated values" class="text-secondary"  v-tippy="{placement: 'top'}" /></sup>
                            </label>
                            <b-form-input
                                v-model="selectedPrintableModel.merlin_ids"
                                :disabled="!can_update_printable"
                            />
                        </b-form-group>
                        <b-form-group
                            label="Tags"
                            label-for="tags"
                        >
                            <tag-multiselect
                                v-model="selectedPrintableModel.tags"
                                :options="tags"
                                @select="addTag"
                                @remove="removeTag"
                                :disabled="!can_update_printable"
                            />
                        </b-form-group>

                        <b-form-group
                            label="Student Directions"
                            label-for="student_directions"
                        >
                            <b-form-textarea
                                v-model="selectedPrintableModel.student_directions"
                                rows="2"
                                max-rows="6"
                                :disabled="!can_update_printable"
                            />
                        </b-form-group>

                        <b-form-group
                            label="Printable Text"
                            label-for="worksheet_text"
                        >
                            <b-form-textarea
                                v-model="selectedPrintableModel.worksheet_text"
                                rows="2"
                                max-rows="6"
                                :disabled="!can_update_printable"
                            />
                        </b-form-group>

                        <b-form-group
                            label="Printable Suggestions"
                            label-for="worksheet_suggestions"
                        >
                            <b-form-textarea
                                v-model="selectedPrintableModel.worksheet_suggestions"
                                rows="2"
                                max-rows="6"
                                :disabled="!can_update_printable"
                            />
                        </b-form-group>

                        <b-form-group
                            label="Artist Description"
                            label-for="artist_description"
                        >
                            <b-form-textarea
                                v-model="selectedPrintableModel.artist_description"
                                rows="2"
                                max-rows="6"
                                :disabled="!can_update_printable"
                            />
                        </b-form-group>

                        <b-form-group>
                            <div class="d-flex justify-content-between">
                                <span class="mb-2">Expiration Date</span>
                            </div>
                            <div id="exp-date-input">
                                <input
                                    type="date"
                                    class="form-control"
                                    :class="{ 'bg-danger text-white': isExpired }"
                                    :value="(selectedPrintableModel.expiration_date || '').slice(0,10)"
                                    @input="onPrintableExpirationDateInput"
                                />
                            </div>
                        </b-form-group>

                        <b-form-group
                            label="Expiration Date Info"
                            label-for="expiration_date_info"
                        >
                            <b-form-textarea
                                v-model="selectedPrintableModel.expiration_date_info"
                                rows="2"
                                max-rows="6"
                                :disabled="!can_update_printable"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col v-if="selectedPrintableModel.cdn_url" cols="5" class="d-flex align-items-center justify-content-center position-relative p-0 mb-5">
                        <div class="pdf-container">
                            <div class="mb-2">
                                <b-link :href="selectedPrintableModel.cdn_url" target="_blank" >
                                    View Printable File In New Tab
                                </b-link>
                            </div>
                            <embed
                                :src="selectedPrintableModel.cdn_url"
                                width="100%"
                                height="100%"
                            >
                        </div>
                    </b-col>
                </b-row>
            </b-container>

            <div class="row align-items-center">
                <div class="w-100">
                    <MediaPedigree :editing_media_pedigree_data="editing_printable_pedigree_data" :is_printables="true" :pedigree_loading="pedigree_loading" />
                </div>
            </div>

            <template #modal-footer>
                <div v-if="!uploading_media">
                    <b-button
                        variant="primary"
                        @click="updatePrintable"
                        :disabled="!can_update_printable || validating()"
                    >
                        Update Printable
                    </b-button>

                    <span id="disabled-delete-btn">
                        <b-button
                            v-if="can_delete_printable"
                            variant="danger"
                            @click="deletePrintable"
                            :disabled="delete_media_disabled"
                            class="ml-2"
                        >
                            Delete Printable
                        </b-button>
                    </span>
                    <b-tooltip v-if="delete_media_disabled" target="disabled-delete-btn">Media must be removed from all publication, units, weeks and articles.</b-tooltip>
                </div>
                <div v-else>
                    <b-spinner small label="Uploading..."></b-spinner>
                    Uploading File - This may take a few minutes
                </div>
                <b-button
                    variant="light"
                    @click="cancel"
                    :disabled="uploading_media"
                >
                    Cancel
                </b-button>
            </template>

        </b-modal>

        <CreateAlternatePrintable
            v-model="show_create_alternate_printable_modal"
            :value="null"
            :printable_to_link="selectedPrintableModel"
            :printables_group="static_printables_array"
            @hideCreateAlternate="show_create_alternate_printable_modal = false"
            @alternateAdded="handleAlternateAdded"
        />

        <AddAlternatePrintable
            v-model="show_add_alternate_printable_modal"
            :value="null"
            :alternate_printables_options="alternate_printables_options"
            :alternate_printables_options_loading="alternate_printables_options_loading"
            :printable_to_link="selectedPrintableModel"
            :printable_group="static_printables_array"
            @hideAddAlternate="show_add_alternate_printable_modal = false"
            @alternateAdded="handleAlternateAdded"
        />
    </div>
</template>

<style lang="scss" scoped>

.copy-btn {
    right: 25px;
}

.pdf-container {
    width: 100%;
    height: 635px;
}

.add-language {
    margin-left: 3px;
    margin-right: 3px;
}

</style>

<script>
import TagMultiselect from '@/components/TagMultiselect'
import { mapState } from 'vuex'

import MediaPedigree from '../../media/MediaPedigree.vue'
import CreateAlternatePrintable from '@/components/modals/media/CreateAlternatePrintable'
import AddAlternatePrintable from '@/components/modals/media/AddAlternatePrintable'

import languages from '@/common/languages'
import copy from 'copy-html-to-clipboard'
import { convertCdnUrlToAWS } from '@/common/urlHelpers'

export default {
    data: () => ({
        submit_data: {
            title: '',
            student_directions: '',
            caption: '',
            credit: '',
            // source_info: '',
            language: '',
            splash_file: null,
            type: 'video',
            file: null,
            url: null,
        },
        show_create_alternate_printable_modal: false,
        languages: languages,
        uploading_media: false,
        alternate_printables_options: [],
        alternate_printables_options_loading: false,
        alternate_printables: [],
        copying_to_clipboard: false,
        alternate_printables_loading: false,
        editing_printable_pedigree_data: null,
        show_media_pedigree: true,
        show_panel_media_pedigree: true,
        show_add_alternate_printable_modal: false,
        pedigree_loading: false,
        static_printables_array: [],
        selected_printable_index: 0,
        printableChanged: false,
        cached_printable: {},
    }),
    props: [
        'editing_printable',
    ],
    components: {
        TagMultiselect,
        MediaPedigree,
        AddAlternatePrintable,
        CreateAlternatePrintable
    },
    computed: {
        ...mapState({
            user: state => state.user.user,
            tags: state => state.tags.tags,
            selectedPrintable: state => state.printables.selectedPrintable,
        }),
        selectedPrintableModel: {
            get() {
                return this.selectedPrintable;
            },
            set(value) {
                this.$store.commit('setSelectedPrintable', value);
            },
        },
        modal_id () {
            let modal_id = 'add-related-media-modal'
            return modal_id
        },
        delete_media_disabled (){
            let disabled = true;
            if (this.pedigree_loading) {
                disabled = true;
            } else if (!this.editing_printable_pedigree_data || this.editing_printable_pedigree_data.length == 0) {
                disabled = false;
            }

            return disabled;
        },
        has_english_alt() {
            if (this.selectedPrintableModel.language != 'en') {
                const hasEnglishPrintable = this.static_printables_array.filter(item => item.id !== this.selectedPrintableModel.id).some(item => item.language === 'en');

                return hasEnglishPrintable;
            }
            return true;
        },
        languageLabel() {
            const languageObject = this.languages.find(lang => lang.language === this.selectedPrintableModel.language);
            return languageObject ? languageObject.label : null;
        },
        can_create_printable() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'add_printable')
        },
        can_update_printable() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_printable')
        },
        can_link_printable() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'link_printable')
        },
        can_unlink_printable() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'unlink_printable')
        },
        can_delete_printable() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'delete_printable')
        },
        existing_file_name(){
            if(this.selectedPrintableModel.cdn_url){
                return this.selectedPrintableModel.cdn_url.replace(/^.*[\\\/]/, '');
            }
            else{
                return '';
            }
        },
        s3FileUrl() {
            return convertCdnUrlToAWS(this.selectedPrintableModel.cdn_url)
        },
        languageOptions() {
            return this.languages.map(item => item.language);
        },
        copy_to_clipboard_btn_variant () {
            let variant = "light"

            if (this.copying_to_clipboard) {
                variant = 'success'
            }

            return variant
        },
        isExpired() {
            const expirationDate = this.selectedPrintableModel && this.selectedPrintableModel.expiration_date;
            if (!expirationDate) {
                return false;
            }
            const [ymd] = String(expirationDate).split(' ');
            if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
                 return false;
            }
            const endOfDay = new Date(`${ymd}T23:59:59`);

            return !isNaN(endOfDay) && Date.now() > endOfDay.getTime();
        },
    },
    watch: {
        selectedPrintableModel: {
            handler(newPrintable, oldPrintable) {
                // this checks to see if the printable has been updated/changed
                this.printableChanged = !(_.isEqual(this.selectedPrintableModel, this.cached_printable))
            },
            deep: true,
        },
        'selectedPrintableModel.id': {
            handler(newId, oldId) {
                // handle all the fetching of new data for the new media when the id changes
                this.getAlternatePrintablesOptions();
                this.getPedigreeData();
                this.submit_data.file = null;
            },
            deep: true,
        },
        'submit_data.file': {
            handler(oldFile, newFile) {
                if (newFile !== '') {
                    this.printableChanged = true;
                }
            },
            deep: true,
        },
    },
    methods: {
        selectPrintable(index) {
            // check to see if current selected media changed
            if (this.printableChanged) {
                this.$swal({
                    html: 'There are unsaved changes for this printable, would you like to save before switching printable languages?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Update Printable and Switch Languages',
                    confirmButtonColor: '#27AE60',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.updatePrintable()
                        this.switchPrintable(index);
                    }
                })
            } else {
                this.switchPrintable(index);
            }
        },
        getFlagIso(language) {
            const languageObject = this.languages.find(lang => lang.language === language);
            return languageObject ? languageObject.flag_iso : null;
        },
        switchPrintable(index) {
            // save modified data to the static_printables_array
            this.static_printables_array[this.selected_printable_index] = Object.assign({}, this.selectedPrintableModel)
            // change index
            this.selected_printable_index = index;
            // set the selected Media
            this.$store.commit("setSelectedPrintable", this.static_printables_array[index]);
            if (Array.isArray(this.selectedPrintableModel.merlin_ids)) {
                this.selectedPrintableModel.merlin_ids = this.selectedPrintableModel.merlin_ids.join(', ')
            }
            // cache media to track changes for new selectedMedia
            this.cached_printable = Object.assign({}, this.selectedPrintableModel);
            if (Array.isArray(this.cached_printable.merlin_ids)) {
                this.cached_printable.merlin_ids = this.cached_printable.merlin_ids.join(', ')
            }
        },
        handleAlternateAdded() {
            this.getAlternatePrintables();
        },
        getAlternatePrintablesOptions(){
            this.alternate_printables_options_loading = true;
            this.axios.get('/api/media/get_alternate_printables_options/'+this.selectedPrintableModel.language)
            .then(response => {
                if (response.data) {
                    this.alternate_printables_options = response.data;
                }
                this.alternate_printables_options_loading = false;
            })
            .catch(error => {
                if (error) {
                    this.alternate_printables_options_loading = false;
                }
            })
        },
        async getPedigreeData() {
            // Check if there's an ongoing API call
            if (this.axiosCancelToken) {
                // Cancel the ongoing API call
                this.axiosCancelToken.cancel('Operation canceled by the user.');
            }

            // Set up a new cancellation token for the current request
            this.axiosCancelToken = this.axios.CancelToken.source();

            // Start the new API call

            try {
                this.pedigree_loading = true;

                const response = await this.axios.post('/api/media/get_all_printable_pedigree/'+this.selectedPrintableModel.id, {
                    cdn_url: this.selectedPrintableModel.cdn_url
                }, {
                    cancelToken: this.axiosCancelToken.token,
                });

                if (response.data) {
                    this.editing_printable_pedigree_data = response.data;
                    this.pedigree_loading = false;
                }
            } catch (error) {
                if (error && !this.axios.isCancel(error)) {
                    this.editing_printable_pedigree_data = [];
                    this.pedigree_loading = false;
                }
            }
        },
        getAlternatePrintables(){
            this.alternate_printables_loading = true;
            this.axios.get('/api/media/get_alternate_printables/'+this.editing_printable.id+'/'+null)
            .then(response => {
                if (response.data) {
                    this.alternate_printables = response.data;
                    this.static_printables_array = [this.editing_printable, ...this.alternate_printables];
                }
                this.alternate_printables_loading = false;
            })
            .catch(error => {
                if (error) {
                    this.alternate_printables = [];
                }
                this.alternate_printables_loading = false;
            })
        },
        addTag (tag) {
            this.axios.post('/api/media/add_printable_tag/' + this.selectedPrintableModel.id, {
                tag_id: tag.id
            })
        },
        removeTag (tag) {
            this.axios.post('/api/media/remove_printable_tag/' + this.selectedPrintableModel.id, {
                tag_id: tag.id
            })
        },
        validating(){
            const { title, language } = this.selectedPrintableModel;
            const languageCheck = this.languageCheck(this.selectedPrintableModel.language);

            return !(title && language && languageCheck);
        },
        addLanguage(new_language){
            this.submit_data.language = new_language;
        },
        removeLanguage(){
            this.submit_data.language = {};
        },
        focusSearch () {
            if (
                this.$refs['related-media-search'] &&
                this.$refs['related-media-search'].$refs['search-term']
            ) {
                this.$refs['related-media-search'].$refs['search-term'].focus()
            }
        },
        cancel(event) {
            event.preventDefault();
            if (this.printableChanged) {
                this.$swal({
                    html: 'There are unsaved changes for this printable, would you like to save before closing?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Update Printable and Close',
                    confirmButtonColor: '#27AE60',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.selected_media_index = 0;
                        this.cached_printable = Object.assign({}, this.selectedPrintableModel);
                        this.updatePrintable(event)
                    }
                })
            } else {
                this.selected_media_index = 0;
                this.cached_printable = Object.assign({}, this.selectedPrintableModel);
                this.$emit('getPrintables');
                this.$emit('hideUploadPrintable');
                this.hide()
            }
        },
        hide () {
            this.selected_media_index = 0;
            this.cached_printable = Object.assign({}, this.selectedPrintableModel);
            this.$emit('hideUploadPrintable')
        },
        hidden () {
            this.selected_media_index = 0;
            this.cached_printable = Object.assign({}, this.selectedPrintableModel);
            this.$emit('hideUploadPrintable')
        },
        updatePrintable(event){
            let file = null;
            let form_data = new FormData();
            file = this.submit_data.file;

            let merlin_id_array = [];
            if(this.selectedPrintableModel.merlin_ids){
                // format comma separated merlin_ids input into an array, only accept numbers
                merlin_id_array = this.selectedPrintableModel.merlin_ids
                    .split(',')
                    .map((id) => parseInt(id.trim(), 10))
                    .filter((id) => !isNaN(id));
            }

            if(file) form_data.append('file', file);
            if(this.selectedPrintableModel.title && this.selectedPrintableModel.title != '') form_data.append('title', this.selectedPrintableModel.title);

            if(this.selectedPrintableModel.language && this.selectedPrintableModel.language != '') form_data.append('language', this.selectedPrintableModel.language);
            if(this.selectedPrintableModel.student_directions && this.selectedPrintableModel.student_directions != '') form_data.append('student_directions', this.selectedPrintableModel.student_directions);
            if(this.selectedPrintableModel.worksheet_text && this.selectedPrintableModel.worksheet_text != '') form_data.append('worksheet_text', this.selectedPrintableModel.worksheet_text);
            if(this.selectedPrintableModel.worksheet_suggestions && this.selectedPrintableModel.worksheet_suggestions != '') form_data.append('worksheet_suggestions', this.selectedPrintableModel.worksheet_suggestions);
            if(this.selectedPrintableModel.artist_description && this.selectedPrintableModel.artist_description != '') form_data.append('artist_description', this.selectedPrintableModel.artist_description);
            if(this.selectedPrintableModel.expiration_date && this.selectedPrintableModel.expiration_date != '') form_data.append('expiration_date', this.selectedPrintableModel.expiration_date);
            form_data.append('expiration_date_info', this.selectedPrintableModel.expiration_date_info || '');
            if (merlin_id_array.length > 0) form_data.append('merlin_ids', merlin_id_array);

            form_data.append('printable_id', this.selectedPrintableModel.id);
            form_data.append('existing_cdn_url', this.selectedPrintableModel.cdn_url);
            form_data.append('existing_file_name', this.existing_file_name);


            this.uploading_media = true;
            this.axios.post('/api/media/update_printable/', form_data)
            .then(response => {
                this.uploading_media = false;
                if(!response){
                    this.$root.$bvToast.toast('An error has ocurred', {
                        title: 'Error',
                        variant: 'danger',
                        solid: false,
                        autoHideDelay: 3000,
                        appendToast: true
                    })
                }
                else{
                    this.$root.$bvToast.toast('Media was successfully updated', {
                        title: 'Success',
                        variant: 'success',
                        solid: false,
                        autoHideDelay: 3000,
                        appendToast: true
                    })
                }
                if (event) {
                    this.$emit('hideUploadPrintable');
                    this.$emit('closeUpload');
                    this.$emit('getPrintables');
                    this.hide();

                }

            })
            .catch(error => {
                if (error) {
                    this.uploading_media = false;
                }
            });
        },
        unlinkFromAlternatePrintable(){
                //unlink from all printables - set group_id to null
                this.axios.post('/api/media/unlink_printable_from_group/'+this.selectedPrintableModel.id)
                .then(response => {
                    if(!response){
                        this.$root.$bvToast.toast('An error has ocurred', {
                            title: 'Error',
                            variant: 'danger',
                            solid: false,
                            autoHideDelay: 3000,
                            appendToast: true
                        })
                    }
                    else{
                        this.$root.$bvToast.toast('Media was successfully unlinked', {
                            title: 'Success',
                            variant: 'success',
                            solid: false,
                            autoHideDelay: 3000,
                            appendToast: true
                        })
                    }
                    this.selectPrintable(0);
                    this.getAlternatePrintables();
                })
        },
        deletePrintable(event){

            this.$swal({
                html: 'Are you sure you want delete this printable?',
                showCancelButton: true,
                confirmButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#E67E22',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.axios.post('/api/media/delete_printable/'+this.selectedPrintableModel.id)
                    .then(response => {
                        if(!response){
                            this.$root.$bvToast.toast('An error has ocurred', {
                                title: 'Error',
                                variant: 'danger',
                                solid: false,
                                autoHideDelay: 3000,
                                appendToast: true
                            })
                        }
                        else{
                            this.$root.$bvToast.toast('Printable was successfully deleted', {
                                title: 'Success',
                                variant: 'success',
                                solid: false,
                                autoHideDelay: 3000,
                                appendToast: true
                            })
                        }

                        this.$emit('getPrintables');
                        this.hide();

                    })
                    .catch(error => {
                        if (error) {
                            this.uploading_media = false;
                        }
                    });
                }
            });

        },
        copyToClipboard (media) {
            let media_url = media.cdn_url;
            if (!(media_url.startsWith("http:") || media_url.startsWith("https:"))) {
                media_url = `https:${media_url}`;
            }
            copy(media_url, {
                asHtml: false
            })

            this.copying_to_clipboard = true

            setTimeout(() => {
                this.copying_to_clipboard = false
            }, 1500)
        },
        addAlternatePrintable() {
            this.show_add_alternate_printable_modal = true;
        },
        createAlternatePrintable() {
            this.show_create_alternate_printable_modal = true;
        },
        languageCheck(language) {
            return !this.static_printables_array.filter(item => item.id !== this.selectedPrintableModel.id).some(item => item.language === language)
        },
        onPrintableExpirationDateInput(event) {
            const ymd = event.target.value;
            const time =
                (this.selectedPrintableModel.expiration_date || '').split(' ')[1] ||
                '00:00:00';

            this.selectedPrintableModel.expiration_date = ymd
                ? `${ymd} ${time}`
                : null;
        },
    },
    mounted () {
        // converts merlin ids from array to csv
        if (Array.isArray(this.selectedPrintableModel.merlin_ids)) {
            this.selectedPrintableModel.merlin_ids = this.selectedPrintableModel.merlin_ids.join(', ')
        }

        this.getPedigreeData();
        this.getAlternatePrintablesOptions();
        this.getAlternatePrintables();
        this.cached_printable = this.editing_printable;
    }
}
</script>
